import { QIcon_chevron_bold_right } from '@repo/icons/index'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useCustomerType } from '../../../modules/apis/student/products'
import { useAppliedContentPrograms } from '../../../modules/apis/student/study/readWithMe/queries'
import { contentProgramTypes } from '../../../modules/apis/student/study/readWithMe/types'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { LOCALE_EN, LOCALE_KO } from '../../../modules/i18n/config'
import { n_common, n_myringle } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import client from '../../../modules/sanityClient'
import { BLOG_NO_RESULT } from '../../../modules/svg/svg'
import urls from '../../../modules/urls'
import { COLORS } from '../../../modules/vars'
import { PREV_READ_TOGETHER } from '../../../sanity/groqQueries'
import { PREV_READ_TOGETHERResult } from '../../../sanity/types'
import { checkApplicationStatus } from '../study/ContentTypeUtils'
import ReadProgramCard, { ReadProgramCardSkeleton } from '../study/ReadProgramCard'

export const checkWebinarStatus = (webinarDates) => {
  const currentDate = new Date()
  const array = webinarDates?.map((dateString) => {
    const webinarDate = new Date(dateString)

    // 마지막 날짜에 대해 1시간 추가
    if (dateString === webinarDates[webinarDates.length - 1]) {
      webinarDate.setHours(webinarDate.getHours() + 1)

      return currentDate > webinarDate
    }
  })

  if (array) return array[array?.length - 1]
}

const SectionPrevReadTogether = () => {
  const router = useAppRouter()
  const { data: customerType } = useCustomerType()
  const { t, isLocaleKO } = useTranslation()
  const getFullUrl = useReturnFullUrl()
  const [ids, setIds] = useState([])

  const { data } = useAppliedContentPrograms(contentProgramTypes.ContentReadTogether)
  const { data: contentPrograms, isFetching }: any = useQuery({
    queryKey: ['prev_read_together'],
    queryFn: () => fetchContentReadTogether(ids),
    enabled: ids.length > 0,
  })

  const fetchContentReadTogether = async (ids) => {
    const params = {
      ids,
      lang: isLocaleKO ? LOCALE_KO : LOCALE_EN,
    }

    const data: PREV_READ_TOGETHERResult[] = await client.fetch(PREV_READ_TOGETHER, params)
    return data
  }

  useEffect(() => {
    if (data) {
      setIds(data.sanity_ids)
    }
  }, [data])

  if (customerType?.isMulticampus) return <></>

  if (isFetching) {
    return (
      <div className="grid grid-cols-2 gap-[16px]">
        <ReadProgramCardSkeleton />
        <ReadProgramCardSkeleton />
      </div>
    )
  }

  return (
    <div className="px-[16px] sm:px-0">
      <div className="mb-[16px] flex flex-row justify-between">
        <div className="Title1-20Bd">{t(n_myringle.rwm.title(contentPrograms?.length || 0))}</div>
        {contentPrograms?.length > 0 && (
          <div
            className="hidden cursor-pointer flex-row items-center sm:flex"
            onClick={() => router.push(urls.student.newPortal.myRingle.prevReadTogether)}
          >
            <div className="Body1-16Bd pt-[2px] text-gray-500">{t(n_common.btn_all)}</div>
            <QIcon_chevron_bold_right color={COLORS.GRAY500} width={16} height={16} />
          </div>
        )}
      </div>
      <div className="">
        {ids.length == 0 ? (
          <div className="mt-[24px] flex flex-col items-center justify-center sm:mt-0">
            <div className="">
              <img src={BLOG_NO_RESULT} alt="blog" className="mb-[20px]" />
            </div>
            <div className="Title2-18Md text-center text-gray-500">{t(n_myringle.rwm.blank)}</div>
          </div>
        ) : contentPrograms && contentPrograms.length == 0 ? (
          <div className="mt-[24px] mb-[24px] flex flex-col items-center justify-center sm:mt-0">
            <div className="">
              <img src={BLOG_NO_RESULT} alt="blog" className="mb-[20px]" />
            </div>
            <div className="Title2-18Md text-center text-gray-500">{t(n_myringle.rwm.blank)}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-2">
            {contentPrograms?.map((program, i) => {
              return (
                <ReadProgramCard
                  key={i}
                  title={program.title}
                  isFinished={checkWebinarStatus(program.liveWebinarDateList.liveWebinarDate)}
                  isReplay={program.replayYoutubeLinkList?.length > 0}
                  isAvailable={checkApplicationStatus(program?.startDate)}
                  isEnrolled={true}
                  thumbnail={program.thumbnail}
                  startDate={program.startDate}
                  endDate={program.endDate}
                  onClickCard={() =>
                    router.push(urls.student.newPortal.study.readTogether.detail(program.slug.current), {
                      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                      appSendData: {
                        url: getFullUrl(urls.webview.study.readTogether.detail(program.slug.current)),
                      },
                    })
                  }
                />
              )
            })}
          </div>
        )}
        {contentPrograms?.length > 0 && (
          <div
            className="border-t-1 mt-[24px] flex cursor-pointer flex-row items-center justify-center py-[20px] sm:hidden"
            onClick={() =>
              router.push(urls.student.newPortal.myRingle.prevReadTogether, {
                appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                appSendData: { url: getFullUrl(urls.webview.myRingle.prevReadTogether) },
              })
            }
          >
            <div className="Body2-14Bd pt-[2px] text-gray-500">{t(n_common.btn_all)}</div>
            <QIcon_chevron_bold_right color={COLORS.GRAY500} width={16} height={16} />
          </div>
        )}
      </div>
    </div>
  )
}

export default SectionPrevReadTogether
